import {useEffect, useState} from "react";
import moment from "moment";

import EmptyData from "@/components/EmptyData";
import SwitchView from "@/components/SwitchView";
import VLFModal from "@/components/Modal";
import VLFSelectNew from "@/components/Input/SelectNew";
import VLFTextField from "@/components/Input/TextField";
import VLFDatePicker from "@/components/Input/DatePicker";

import {RosterAPI} from "@/api/roster";
import {MatchAPI} from "@/api/match";
import {PlayerAPI} from "@/api/player";

import {REQUIRED_FIELD, AREA_OPTIONS} from "@/utils/constants";
import {
	buildPlayerBody,
	checkRequiredFields,
	getTextField,
	playerToOption,
} from "@/utils/functions";
import {useTranslation} from "react-i18next";

import "./handleReport.scss";

// ── TIT / RIS toggle ──────────────────────────────────────────────────────────

function TitRisToggle({value, onChange}) {
	return (
		<div className="hr-tit-wrap">
			{[
				{val: true, label: "TIT"},
				{val: false, label: "RIS"},
			].map(({val, label}) => (
				<button
					key={label}
					type="button"
					className={`hr-tit-btn hr-tit-btn--${val ? "tit" : "ris"}${value === val ? " hr-tit-btn--active" : ""}`}
					onClick={() => onChange(val)}
				>
					{label}
				</button>
			))}
		</div>
	);
}

// ── Main component ────────────────────────────────────────────────────────────

function HandleReportModal(props) {
	const {
		open,
		confirm,
		close,
		initialData,
		players,
		excludedPlayerIds = [],
	} = props;

	const {t} = useTranslation();

	const [formData, setFormData] = useState({});
	const [errors, setErrors] = useState([]);
	const [selectionError, setSelectionError] = useState("");
	const [rosterPlayers, setRosterPlayers] = useState([]);
	const [sources, setSources] = useState([]);
	const [source, setSource] = useState(null);
	const [sourcePlayers, setSourcePlayers] = useState([]);
	const [sourcesLoading, setSourcesLoading] = useState(false);
	const [sourceLoading, setSourceLoading] = useState(false);
	const [sourceError, setSourceError] = useState("");
	useEffect(() => {
		if (!open || !initialData?.isInternal) return;
		let active = true;
		setSource(null);
		setSourceLoading(false);
		setSourcePlayers([]);
		setSources([]);
		setSourceError("");
		setSourcesLoading(true);
		MatchAPI.getConvocationRosters(initialData.matchId, initialData.matchSide)
			.then((rows) => {
				if (active) setSources(rows || []);
			})
			.catch(() => {
				if (active)
					setSourceError(
						"Impossibile caricare le rose abilitate. Chiudi e riapri per riprovare.",
					);
			})
			.finally(() => {
				if (active) setSourcesLoading(false);
			});
		return () => {
			active = false;
		};
	}, [
		open,
		initialData?.isInternal,
		initialData?.matchId,
		initialData?.matchSide,
	]);
	useEffect(() => {
		if (!open || !initialData?.isInternal || !source?.value) return;
		let active = true;
		setSourcePlayers([]);
		setSourceError("");
		setSourceLoading(true);
		RosterAPI.getById(source.value)
			.then((roster) => {
				if (active)
					setSourcePlayers(
						(roster.players || []).map((row) => ({
							...row.player,
							roster: {id: roster.id},
							clubId: roster.clubId,
						})),
					);
			})
			.catch(() => {
				if (active)
					setSourceError(
						"Impossibile caricare i giocatori. Seleziona nuovamente la rosa per riprovare.",
					);
			})
			.finally(() => {
				if (active) setSourceLoading(false);
			});
		return () => {
			active = false;
		};
	}, [open, initialData?.isInternal, source?.value]);
	const [recognition, setRecognition] = useState("");
	const [recognizing, setRecognizing] = useState(false);
	useEffect(() => {
		let active = true;
		setRosterPlayers([]);
		setRecognition("");
		if (
			!open ||
			!initialData?.linkParticipationId ||
			!initialData?.matchRosterId
		) {
			setRecognizing(false);
			return;
		}
		setRecognizing(true);
		RosterAPI.getById(initialData.matchRosterId)
			.then((roster) => {
				if (!active) return;
				const rows = roster.players || [];
				setRosterPlayers(
					rows.map((r) => ({
						...r.player,
						roster: {id: roster.id},
						clubId: roster.clubId,
					})),
				);
				const normalize = (v) =>
					(v || "").trim().replace(/\s+/g, " ").toLocaleLowerCase("it");
				const name = normalize(initialData.playerName);
				const matches = rows.filter((r) => {
					const p = r.player;
					const sameName =
						name &&
						[
							normalize(p.firstName + " " + p.lastName),
							normalize(p.lastName + " " + p.firstName),
						].includes(name);
					return name
						? sameName
						: r.jerseyNumber != null &&
								r.jerseyNumber === initialData.playerNumber;
				});
				if (matches.length === 1) {
					setFormData((prev) => ({
						...prev,
						player: playerToOption(matches[0].player, true),
					}));
					setRecognition(
						name
							? "Anagrafica individuata nella rosa. Verifica il giocatore e conferma titolare o riserva per completare il collegamento."
							: "Giocatore proposto dal numero di maglia nella rosa. Verifica l’identità e conferma titolare o riserva.",
					);
				} else
					setRecognition(
						matches.length
							? "Più anagrafiche possibili nella rosa: scegli il giocatore e conferma titolare o riserva."
							: "Nessuna corrispondenza univoca nella rosa: scegli l’anagrafica da collegare oppure inseriscila.",
					);
			})
			.catch(() => {
				if (active)
					setRecognition(
						"Impossibile leggere la rosa: seleziona e verifica manualmente l’anagrafica.",
					);
			})
			.finally(() => {
				if (active) setRecognizing(false);
			});
		return () => {
			active = false;
		};
	}, [
		open,
		initialData?.linkParticipationId,
		initialData?.matchRosterId,
		initialData?.playerName,
		initialData?.playerNumber,
	]);

	const [insertType, setInsertType] = useState(
		!initialData?.isInternal ? "single" : "multiple",
	);
	const [viewType, setViewType] = useState("select");

	const clubPlayers = [...(players ?? []), ...rosterPlayers].filter(
		(p) =>
			p.clubId === initialData?.clubId ||
			p.roster?.club?.id === initialData?.clubId,
	);

	// `players` è già filtrata a monte (index.js): tutti gli interni a
	// disposizione per il club interno, database scouting per il club esterno.
	const selectPlayers = [
		...new Map(
			(initialData?.isInternal && source?.value
				? sourcePlayers
				: [...(players || []), ...rosterPlayers]
			)
				.filter((p) => !excludedPlayerIds.includes(p.id))
				.map((p) => [p.id, p]),
		).values(),
	];

	const getFormattedPlayer = (p) => playerToOption(p, true);

	const formattedPlayers = selectPlayers?.map(getFormattedPlayer);

	useEffect(() => {
		setFormData(
			initialData?.linkParticipationId
				? {playerNumber: initialData.playerNumber}
				: {},
		);
		setErrors([]);
		setSelectionError("");
		setInsertType(initialData?.isInternal ? "multiple" : "single");
		setViewType("select");
	}, [
		open,
		initialData?.isInternal,
		initialData?.linkParticipationId,
		initialData?.playerNumber,
	]);

	const updatePlayerField = (playerId, key, val) =>
		setFormData((f) => ({
			...f,
			player: {
				...f.player,
				[playerId]: {...f.player?.[playerId], [key]: val},
			},
		}));

	const createPlayer = async () => {
		try {
			const body = {
				...buildPlayerBody({
					firstName: formData?.firstName,
					lastName: formData?.lastName,
					dateOfBirth: formData?.dateOfBirth,
					goalkeeper: formData?.playerPrimaryRole?.value === "Goalkeeper",
					primaryRole: formData?.playerPrimaryRole?.label,
				}),
				clubId: initialData?.clubId,
			};
			return await PlayerAPI.create(body);
		} catch {
			// silenzioso: la confirm gestisce l'assenza di id
		}
	};

	const requiredFields =
		insertType === "multiple"
			? []
			: viewType === "provisional"
				? ["playerNumber"]
				: initialData?.isInternal ||
					  viewType === "select" ||
					  viewType === "club"
					? ["player", "playerNumber"]
					: ["lastName", "dateOfBirth", "playerPrimaryRole", "playerNumber"];

	const switchOptions = [
		{value: "new", label: "Nuovo"},
		{value: "club", label: "Da club"},
		{value: "select", label: "Da database"},
		...(!initialData?.linkParticipationId
			? [{value: "provisional", label: "Provvisorio"}]
			: []),
	];

	const defaultTextField = {
		values: formData,
		setValues: setFormData,
		errors,
		setErrors,
	};

	const sortedPlayers = [...selectPlayers].sort((a, b) =>
		a.lastName.toLowerCase().localeCompare(b.lastName.toLowerCase()),
	);

	return (
		<VLFModal
			maxWidth={insertType === "multiple" ? "md" : "sm"}
			open={open}
			title={
				initialData?.linkParticipationId
					? "Abbina il giocatore della distinta"
					: "Aggiungi informazione"
			}
			close={close}
			disabled={recognizing || sourcesLoading || sourceLoading || !!sourceError}
			confirm={async () => {
				if (sourcesLoading || sourceLoading || sourceError) return;
				if (
					initialData?.linkParticipationId &&
					formData.startingPlayer == null
				) {
					setSelectionError("Indica se il giocatore era titolare o riserva.");
					return;
				}
				const errs = checkRequiredFields(requiredFields, formData);
				setErrors(errs);

				if (!errs.length && viewType === "provisional") {
					const number = Number(formData.playerNumber);
					if (
						formData.playerNumber === "" ||
						!Number.isInteger(number) ||
						number < 0 ||
						number > 999
					) {
						setSelectionError("Indica un numero di maglia intero da 0 a 999.");
						return;
					}
					confirm([
						{
							playerId: null,
							playerNumber: number,
							playerName: formData.playerName?.trim() || null,
						},
					]);
					return;
				}
				if (!errs.length) {
					let body, selectedPlayer;

					if (insertType === "multiple") {
						body = (selectPlayers ?? []).flatMap((p) => {
							const row = formData?.player?.[p.id];
							if (!row?.playerNumber) return [];
							return [
								{
									...row,
									playerId: p.id,
									sourceRosterId: p.roster?.id || null,
									playerYear: moment(p.dateOfBirth).year(),
								},
							];
						});
						if (!body.length) {
							setSelectionError(
								"Indica il numero di maglia di almeno un giocatore da inserire.",
							);
							return;
						}
						confirm(body);
					} else {
						body = {...formData};

						if (viewType === "new") {
							const res = await createPlayer();
							selectedPlayer = res;
							body.player = {
								value: res?.id,
								label: res?.firstName + " " + res?.lastName,
							};
						} else if (viewType === "club") {
							selectedPlayer = clubPlayers?.find(
								(p) => p.id === formData.player?.value,
							);
						} else {
							selectedPlayer = selectPlayers?.find(
								(p) => p.id === formData.player?.value,
							);
						}

						delete body.firstName;
						delete body.lastName;
						delete body.dateOfBirth;
						delete body.playerPrimaryRole;

						confirm([
							{
								...body,
								playerId: body.player.value,
								sourceRosterId: selectedPlayer?.roster?.id || null,
								playerYear: moment(selectedPlayer?.dateOfBirth).year(),
							},
						]);
					}
				}
			}}
			content={
				<div className="row handle-report-modal-container">
					<div className="col-12">
						{selectionError && (
							<div role="alert" className="text-danger mb-2">
								{selectionError}
							</div>
						)}
						{recognition && <p role="status">{recognition}</p>}
						{initialData?.isInternal && (
							<VLFSelectNew
								aria-label="Rosa di provenienza"
								label="Rosa di provenienza"
								placeholder="Giocatori disponibili"
								options={sources.map((r) => ({value: r.id, label: r.name}))}
								value={source}
								isLoading={sourcesLoading}
								isClearable
								onChange={(value) => {
									setSource(value);
									setSourceLoading(!!value?.value);
									setSourcePlayers([]);
									setSourceError("");
									setFormData({});
									setErrors([]);
									setSelectionError("");
								}}
							/>
						)}
						{sourceError && (
							<p role="alert" className="text-danger">
								{sourceError}
							</p>
						)}
						{sourceLoading && <p role="status">Caricamento giocatori...</p>}
						{/* Single / Multiplo toggle (only for internal teams) */}
						{initialData?.isInternal && (
							<SwitchView
								currentSwitchView={insertType}
								setCurrentSwitchView={(value) => {
									setInsertType(value);
									if (value === "multiple")
										setViewType(initialData?.matchCategoryId);
								}}
								pageOptions={[
									{value: "single", label: "Singolo"},
									{value: "multiple", label: "Multiplo"},
								]}
								fixed
								callback={() => {
									setFormData({player: null});
									setSelectionError("");
								}}
							/>
						)}

						{/* Nuovo / Da club / Da database toggle (only for external teams in single mode) */}
						{insertType === "single" && !initialData?.isInternal && (
							<SwitchView
								currentSwitchView={viewType}
								setCurrentSwitchView={setViewType}
								pageOptions={switchOptions}
								fixed
								callback={() => {
									setFormData({player: null});
									setSelectionError("");
								}}
							/>
						)}

						{/* ── MULTIPLE mode ─────────────────────────────── */}
						{insertType === "multiple" ? (
							<>
								{!sortedPlayers.length ? (
									<EmptyData />
								) : (
									<>
										<div className="hr-multi-header">
											<span className="hr-multi-header__name">Giocatore</span>
											<span className="hr-multi-header__number">N° maglia</span>
											<span className="hr-multi-header__role">Ruolo</span>
										</div>
										{sortedPlayers.map((p) => {
											const row = formData?.player?.[p.id];
											return (
												<div key={p.id} className="hr-multi-row">
													<span className="hr-multi-row__name">
														{p.firstName} {p.lastName}
													</span>
													<div className="hr-multi-row__number">
														<VLFTextField
															name="playerNumber"
															readOnly={Boolean(
																initialData?.linkParticipationId,
															)}
															value={row?.playerNumber || ""}
															onChange={(e) =>
																updatePlayerField(
																	p.id,
																	"playerNumber",
																	e.target.value,
																)
															}
															label=""
															placeholder="—"
															size="small"
															type="number"
														/>
													</div>
													<div className="hr-multi-row__role">
														<TitRisToggle
															value={row?.startingPlayer ?? null}
															onChange={(val) =>
																updatePlayerField(p.id, "startingPlayer", val)
															}
														/>
													</div>
												</div>
											);
										})}
									</>
								)}
							</>
						) : (
							// ── SINGLE mode ──────────────────────────────────
							<>
								{initialData?.isInternal || viewType === "select" ? (
									<VLFSelectNew
										aria-label="Giocatore"
										options={formattedPlayers}
										sortKey="sortKey"
										value={formData?.player || ""}
										onChange={(value) => {
											setErrors([]);
											setFormData({...formData, player: value});
										}}
										required
										helperText={errors.includes("player") ? REQUIRED_FIELD : ""}
										error={errors.includes("player")}
										label="Giocatore"
									/>
								) : viewType === "club" ? (
									<>
										{!clubPlayers.length && (
											<div className="handle-report__empty">
												Nessun giocatore trovato per questo club
												{!initialData?.clubId && " (clubId mancante)"}
											</div>
										)}
										<VLFSelectNew
											options={clubPlayers.map((p) => playerToOption(p, true))}
											sortKey="sortKey"
											value={formData?.player || ""}
											onChange={(value) => {
												setErrors([]);
												setFormData({...formData, player: value});
											}}
											required
											helperText={
												errors.includes("player") ? REQUIRED_FIELD : ""
											}
											error={errors.includes("player")}
											label="Giocatore"
										/>
									</>
								) : viewType === "provisional" ? (
									<>
										<p className="dt-proposal-note">
											Il giocatore viene inserito solo nella distinta, senza
											creare un'anagrafica. Potrai collegarlo in seguito.
										</p>
										<VLFTextField
											label="Nome (facoltativo)"
											value={formData.playerName || ""}
											onChange={(e) =>
												setFormData({...formData, playerName: e.target.value})
											}
										/>
									</>
								) : (
									<>
										{getTextField({
											...defaultTextField,
											fieldKey: "firstName",
											label: t("name"),
										})}
										{getTextField({
											...defaultTextField,
											fieldKey: "lastName",
											label: t("surname"),
											required: true,
										})}
										<VLFDatePicker
											label={t("birthdate")}
											value={formData.dateOfBirth}
											onChange={(value) => {
												setErrors([]);
												setFormData({...formData, dateOfBirth: value});
											}}
											format="DD-MM-YYYY"
											required
											helperText={
												errors.includes("dateOfBirth") ? REQUIRED_FIELD : ""
											}
											error={errors.includes("dateOfBirth")}
										/>
										<VLFSelectNew
											options={AREA_OPTIONS}
											sorted={false}
											value={formData.playerPrimaryRole}
											onChange={(newValue) => {
												setErrors([]);
												setFormData({...formData, playerPrimaryRole: newValue});
											}}
											label={t("role")}
											required
											helperText={
												errors.includes("playerPrimaryRole")
													? REQUIRED_FIELD
													: ""
											}
											error={errors.includes("playerPrimaryRole")}
										/>
									</>
								)}

								<VLFTextField
									name="playerNumber"
									value={formData.playerNumber || ""}
									onChange={(event) => {
										setErrors([]);
										setFormData({
											...formData,
											playerNumber: event.target.value,
										});
									}}
									label="N° di maglia"
									size="small"
									type="number"
									required
									helperText={
										errors.includes("playerNumber") ? REQUIRED_FIELD : ""
									}
									error={errors.includes("playerNumber")}
								/>

								{viewType !== "provisional" && (
									<div className="hr-single-role">
										<span className="hr-single-role__label">
											Ruolo in partita
										</span>
										<TitRisToggle
											value={formData?.startingPlayer ?? null}
											onChange={(val) =>
												setFormData({...formData, startingPlayer: val})
											}
										/>
									</div>
								)}
							</>
						)}
					</div>
				</div>
			}
		/>
	);
}

export default HandleReportModal;
